<template>
  <div :class="shellClasses">
    <slot name="before" />
    <textarea ref="textareaRef" :value="modelValue" :rows="rows" :placeholder="placeholder" :disabled="disabled"
      :class="['ui-text-area__control', textareaClass]" @input="handleInput" @focus="emit('focus', $event)"
      @blur="emit('blur', $event)" @keydown="emit('keydown', $event)" @click="emit('click', $event)"
      @keyup="emit('keyup', $event)" @scroll="emit('scroll', $event)" />
    <slot name="after" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';

const props = withDefaults(defineProps<{
  modelValue?: string;
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
  autoResize?: boolean;
  textareaClass?: string | string[] | Record<string, boolean>;
  variant?: 'default' | 'form';
}>(), {
  modelValue: '',
  rows: 1,
  placeholder: '',
  disabled: false,
  autoResize: true,
  textareaClass: '',
  variant: 'default'
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  input: [event: Event];
  focus: [event: FocusEvent];
  blur: [event: FocusEvent];
  keydown: [event: KeyboardEvent];
  click: [event: MouseEvent];
  keyup: [event: KeyboardEvent];
  scroll: [event: Event];
}>();

const textareaRef = ref<HTMLTextAreaElement | null>(null);

const shellClasses = computed(() => [
  'ui-text-area',
  `ui-text-area--${props.variant}`,
  {
    'ui-text-area--disabled': props.disabled
  }
]);

function resize(): void {
  if (!props.autoResize || !textareaRef.value) {
    return;
  }

  textareaRef.value.style.height = 'auto';
  textareaRef.value.style.height = `${textareaRef.value.scrollHeight}px`;
}

function focus(): void {
  textareaRef.value?.focus();
}

function setSelectionRange(start: number, end: number): void {
  textareaRef.value?.setSelectionRange(start, end);
}

function handleInput(event: Event): void {
  const target = event.target as HTMLTextAreaElement;
  emit('update:modelValue', target.value);
  emit('input', event);
  resize();
}

watch(() => props.modelValue, async () => {
  await nextTick();
  resize();
});

onMounted(() => {
  resize();
});

defineExpose({
  focus,
  resize,
  setSelectionRange,
  textareaRef
});
</script>